const swiperAccessibility = () => {
  /**
   * Events that should trigger visibility recalculation.
   * @type {string[]}
   */
  const EVENTS = ["init", "transitionEnd", "resize"];

  /**
   * Selectors used to find focusable elements within a Swiper slide.
   * @type {string}
   */
  const FOCUSABLE_SELECTORS = [
    "a[href]",
    "button:not([disabled])",
    "input:not([disabled])",
    "textarea:not([disabled])",
    "select:not([disabled])",
    '[tabindex]:not([tabindex="-1"])',
  ].join(",");

  /**
   * Toggle the native `inert` attribute on a slide that is entirely hidden
   * @param {Element} slide
   * @param {boolean} partiallyVisible
   * @returns {void}
   */
  const applyInert = (slide, partiallyVisible) => {
    if (!slide) {
      return;
    }
    if (partiallyVisible) {
      slide.removeAttribute("inert");
      return;
    }
    slide.setAttribute("inert", "");
  };

  /**
   * Toggle `aria-hidden` on a slide depending on visibility.
   * @param {Element} slide
   * @param {boolean} isVisible
   * @returns {void}
   */
  const applyAriaHidden = (slide, isVisible) => {
    if (!slide) {
      return;
    }
    if (isVisible) {
      slide.removeAttribute("aria-hidden");
      return;
    }
    slide.setAttribute("aria-hidden", "true");
  };

  /**
   * Adjust visibility attributes (`inert`, `aria-hidden`, `tabindex`) on a slide.
   * @param {Element} slide
   * @param {boolean} partiallyVisible
   * @param {boolean} fullyVisible
   * @returns {void}
   */
  const markSlideVisibility = (slide, partiallyVisible, fullyVisible) => {
    if (!slide) {
      return;
    }
    applyInert(slide, partiallyVisible);
    applyAriaHidden(slide, fullyVisible);

    const focusableItems = slide.querySelectorAll(FOCUSABLE_SELECTORS);
    focusableItems.forEach((focusable) => {
      focusable.setAttribute("tabindex", fullyVisible ? "0" : "-1");
    });
  };

  /**
   * Re-evaluate every slide against the Swiper viewport and update attributes.
   * @param {*} instance
   * @returns {void}
   */
  const updateSlideStates = (instance) => {
    if (!instance || !instance.el || !instance.slides) {
      return;
    }

    const main = document.querySelector("main#main");
    if (!main) {
      return;
    }
    const containerRect = main.getBoundingClientRect();

    const slides = Array.from(instance.slides);

    slides.forEach((slide) => {
      const rect = slide.getBoundingClientRect();
      const partiallyVisible =
        rect.right > containerRect.left && rect.left < containerRect.right;
      const fullyVisible =
        rect.left >= containerRect.left && rect.right <= containerRect.right;
      markSlideVisibility(slide, partiallyVisible, fullyVisible);
    });
  };

  /**
   * Set up accessibility tooling for a Swiper instance by listening to lifecycle events.
   * @param {*} instance
   * @returns {function(): void} Cleanup function that removes listeners.
   */
  const setupAccessibility = (instance) => {
    if (!instance) {
      return () => {};
    }

    const handler = () => updateSlideStates(instance);
    EVENTS.forEach((event) => instance.on(event, handler));
    handler();

    return () => EVENTS.forEach((event) => instance.off(event, handler));
  };

  /**
   * Listen for prev/next button clicks and toggle disabled states.
   * @param {*} instance
   * @param {HTMLButtonElement|null} prevButton
   * @param {HTMLButtonElement|null} nextButton
   * @returns {void}
   */
  const listenButtons = (instance, prevButton, nextButton) => {
    if (!instance) {
      return;
    }

    const updateStates = () => {
      if (prevButton) {
        prevButton.disabled = instance.isBeginning;
      }
      if (nextButton) {
        nextButton.disabled = instance.isEnd;
      }
    };

    updateStates();

    prevButton?.addEventListener("click", () => {
      instance.slidePrev();
      updateStates();
    });

    nextButton?.addEventListener("click", () => {
      instance.slideNext();
      updateStates();
    });

    instance.on("slideChange", updateStates);
  };

  return {
    setupAccessibility,
    listenButtons,
  };
};

export default swiperAccessibility;
